/**
 * Centralized Formatting Utilities
 * All date, time, currency, and other formatters in one place
 */

import { format } from "date-fns";
import { pt } from "date-fns/locale";

// ============================================================================
// PLURAL
// ============================================================================

/**
 * Contagem com o plural certo — em vez do "departamento(s)" que se lia por toda
 * a app. O `(s)` é uma desculpa de programador; o utilizador lê português.
 *
 * O plural irregular passa-se à mão, que o português não o adivinha a partir do
 * singular: `pluralize(1, "colaborador", "colaboradores")`.
 *
 * @example pluralize(14, "departamento")            → "14 departamentos"
 * @example pluralize(1,  "departamento")            → "1 departamento"
 * @example pluralize(3,  "colaborador", "colaboradores") → "3 colaboradores"
 */
export const pluralize = (count: number, singular: string, plural?: string): string =>
  `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;

// ============================================================================
// DATE & TIME FORMATTERS
// ============================================================================

/**
 * Format date in Portuguese medium style
 * @example "15 de jan. de 2024"
 */
export const formatDate = (
  date: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return "—";
  try {
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString("pt-PT", options || defaultOptions);
  } catch {
    return "—";
  }
};

/**
 * Data numérica do locale pt-PT, sem validar: quem chama garante que `iso` é uma data.
 * Não confundir com `formatDate` (formato médio, tolerante a vazio).
 * @example "15/01/2024"
 */
export const formatNumericDate = (iso: string): string => new Date(iso).toLocaleDateString("pt-PT");

/** Como `formatNumericDate`, com "—" quando não há data. */
export const formatNumericDateOrDash = (iso: string | null): string => (iso ? formatNumericDate(iso) : "—");

/**
 * Data e hora numéricas do locale pt-PT.
 * @example "15/01/2024, 14:30:00"
 */
export const formatNumericDateTime = (iso: string): string => new Date(iso).toLocaleString("pt-PT");

/**
 * Data `dd/mm/aaaa` a partir do texto ISO (`aaaa-mm-dd`, com ou sem hora), sem passar por `Date`:
 * um dia civil não muda com o fuso. Vazio dá "—"; texto que não é ISO volta tal como veio.
 * @example "2024-01-15" -> "15/01/2024"
 */
export const formatDateShort = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

/**
 * Data e hora `dd/mm/aaaa, hh:mm` (ano com 4 dígitos, ao contrário de `formatDateTime`). Quem chama garante que `iso` é uma data.
 * @example "15/01/2024, 14:30"
 */
export const formatDateTimeShort = (iso: string): string =>
  new Date(iso).toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Inteiro (ou decimal curto) com separador de milhares pt-PT. @example "12 345" */
export const formatCount = (n: number): string => n.toLocaleString("pt-PT");

/**
 * Format date with full month name
 * @example "15 de janeiro 2024"
 */
export const formatDayLabel = (value: Date | string): string => {
  const date = typeof value === 'string' ? new Date(value) : value;
  return format(date, "dd 'de' MMMM yyyy", { locale: pt });
};

/**
 * Format time from HH:mm string or ISO datetime
 * @example "14:30"
 */
export const formatTime = (value?: string | null): string => {
  if (!value) return "—";
  try {
    // If it's just a time string (HH:mm)
    if (value.length <= 5 && value.includes(':')) {
      return new Intl.DateTimeFormat("pt-PT", {
        timeStyle: "short"
      }).format(new Date(`1970-01-01T${value}`));
    }
    // If it's a full datetime
    return new Intl.DateTimeFormat("pt-PT", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

/**
 * Format date and time together
 * @example "15/01/24, 14:30"
 */
export const formatDateTime = (
  dateString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    const defaultOptions: Intl.DateTimeFormatOptions = {
      dateStyle: "short",
      timeStyle: "short",
    };
    return new Intl.DateTimeFormat("pt-PT", options || defaultOptions).format(date);
  } catch {
    return "—";
  }
};

/**
 * Format date for HTML input[type="date"] (YYYY-MM-DD)
 */
export const formatDateForInput = (date?: string | Date | null): string => {
  if (!date) return "";
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// ============================================================================
// DURATION & TIME PERIOD FORMATTERS
// ============================================================================

/**
 * Format duration in minutes to hours and minutes
 * @example "2 h 30 min", "45 min", "3 h"
 */
export const formatDuration = (minutes?: number | null): string => {
  if (minutes === undefined || minutes === null) return "—";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
};

/**
 * Format duration in minutes to compact notation
 * @example "2h30", "45m", "3h"
 */
export const formatDurationCompact = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, "0")}`;
};

/**
 * Format a SIGNED time-bank balance (minutes) as "−12h30" / "+2h00" / "0h00".
 * Banco de Horas: negativo = a dever, positivo = a favor. Sempre mostra os
 * minutos com 2 dígitos (12h30, não 12h3) e o sinal explícito.
 */
export const formatBalance = (minutes?: number | null): string => {
  if (minutes === undefined || minutes === null) return "—";
  const sign = minutes < 0 ? "−" : minutes > 0 ? "+" : "";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}h${m.toString().padStart(2, "0")}`;
};

/**
 * Calculate and format age from birth date
 * @returns Age in years or null if invalid
 */
export const formatAge = (birthDate?: string | null): number | null => {
  if (!birthDate) return null;
  try {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  } catch {
    return null;
  }
};

// ============================================================================
// CURRENCY FORMATTERS
// ============================================================================

/**
 * Standard currency formatter
 * @example "1.234,56 €"
 */
export const formatCurrency = (
  value: number | string | null | undefined,
  currency: string = "EUR"
): string => {
  // `Decimal` do servidor chega como texto ("1250.00"): converte-se aqui, num só sítio.
  const n = value === null || value === undefined || value === "" ? NaN : Number(value);
  if (!Number.isFinite(n)) return "€0,00";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: currency,
  }).format(n);
};

/**
 * Preço por unidade que anda nas milésimas (ex.: preço por página, 0,0045€).
 * `formatCurrency` arredondaria isto a 2 casas e mostraria "0,00€" para
 * qualquer coisa abaixo de meio cêntimo — este formatador mostra sempre as
 * 4 casas do valor real, nunca arredondado a menos.
 */
export const formatCurrencyPerUnit = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value);
};


/**
 * Compact currency formatter
 * @example "€1,234", "€1.2K", "€1.2M"
 */
export const formatCompactCurrency = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === '') {
    return '€0';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '€0';
  }

  if (num >= 1_000_000) {
    return `€${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `€${(num / 1_000).toFixed(1)}K`;
  }
  return `€${num.toFixed(0)}`;
};

// ============================================================================
// NUMBER FORMATTERS
// ============================================================================

/**
 * Format quantity with decimal places
 * @example "1.234,50"
 */
export const formatQuantity = (value: number | string | undefined | null, decimals: number = 2, empty: string = '0'): string => {
  if (value === null || value === undefined || value === '') return empty;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return empty;
  return new Intl.NumberFormat("pt-PT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

/**
 * Format percentage
 * @example "45.5%", "12.0%"
 */
export const formatPercentage = (value: number | null | undefined, decimals: number = 1): string => {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format a salary range from min/max values
 * @example "1.200€ – 1.800€ / mês", "A partir de 1.200€ / mês"
 */
export const formatSalaryRange = (
  salaryMin: number | null | undefined,
  salaryMax: number | null | undefined
): string | null => {
  const fmt = (n: number) =>
    n.toLocaleString("pt-PT", { minimumFractionDigits: 0 }) + "\u20ac";
  // `!= null` (e não `!== null`) apanha também o `undefined`: um endpoint que
  // omita o campo fazia isto rebentar no `toLocaleString`, e a página pública
  // da vaga ficava em branco.
  if (salaryMin != null && salaryMax != null) {
    return `${fmt(salaryMin)} \u2013 ${fmt(salaryMax)} / mes`;
  }
  if (salaryMin != null) return `A partir de ${fmt(salaryMin)} / mes`;
  if (salaryMax != null) return `Ate ${fmt(salaryMax)} / mes`;
  return null;
};

/** Peso em kg como se lê em Portugal: 0,055 — até 4 casas (uma gola pesa 0,055 kg; duas casas mostravam 0,06). */
export const formatKg = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  return Number(value).toLocaleString("pt-PT", { minimumFractionDigits: 0, maximumFractionDigits: 4, useGrouping: false });
};

/** Dias de carga de um tear: «12,3 d» (uma casa, no máximo). Sem estimativa não se inventa um zero. */
export const formatLoadDays = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  return `${Number(value).toLocaleString("pt-PT", { minimumFractionDigits: 0, maximumFractionDigits: 1 })} d`;
};

/** Quilos arredondados ao inteiro, para totais: «3 205 kg». O painel não precisa da terceira casa (para isso há `formatKg`). */
export const formatKgRounded = (value: number | string | null | undefined): string =>
  value === null || value === undefined || value === "" ? "—" : `${formatQuantity(value, 0)} kg`;

const MONTHS_SHORT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/**
 * Data curta «25 jul». Os meses são escritos aqui (não pelo `Intl`): o texto não pode depender do ICU de quem corre.
 * Um ISO lê-se como dia local, sem escorregar com o fuso.
 */
export const formatDayMonth = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d || m > 12) return "—";
  return `${d} ${MONTHS_SHORT[m - 1]}`;
};

/** `Decimal` do servidor (texto) → número, ou `null` quando vazio/inválido. */
export const decimalToNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Um `Decimal` do servidor (que chega como texto: `"12.5000"`) com `digits` casas, ou «—» quando não há valor.
 * `typeof v === "number"` NÃO serve para estes campos: nunca é número, e a coluna ficava sempre em «—».
 */
export const fixedOrDash = (value: string | number | null | undefined, digits: number, prefix = ""): string => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${prefix}${n.toFixed(digits)}` : "—";
};

/** Tamanho de ficheiro como se lê em Portugal: «512 B», «2 kB», «1,5 MB». Uma só cópia para as listas de anexos. */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024).toLocaleString("pt-PT")} kB`;
  if (bytes < 1024 ** 3) return `${(bytes / (1024 * 1024)).toLocaleString("pt-PT", { maximumFractionDigits: 1 })} MB`;
  return `${(bytes / 1024 ** 3).toLocaleString("pt-PT", { maximumFractionDigits: 1 })} GB`;
};
